import { useMemo, useState } from 'react';

interface UseGuardrailStylesProps {
  isSuccess: boolean;
  message?: string;
}

interface UseGuardrailStylesReturn {
  hasDetails: boolean;
  hasMaskingInfo: boolean;
  isDetailsExpanded: boolean;
  detailsBgColor: string;
  detailsTextColor: string;
  setIsDetailsExpanded: (value: boolean | ((prev: boolean) => boolean)) => void;
}

/**
 * Hook to manage guardrail card styling and expansion logic
 */
export function useGuardrailStyles({
  isSuccess,
  message
}: UseGuardrailStylesProps): UseGuardrailStylesReturn {
  const hasDetails = !!message;
  const hasMaskingInfo = message?.toLowerCase().includes('masked') ?? false;

  // Determine if details should be expanded by default
  // Expand if failed, collapse if success (unless message contains masking info)
  const [isDetailsExpanded, setIsDetailsExpanded] = useState(
    !isSuccess || hasMaskingInfo
  );

  // Determine details background color
  const detailsBgColor = useMemo(() => {
    if (!isSuccess) {
      return 'bg-negative/10'; // Red background for failures
    }
    if (hasMaskingInfo) {
      return 'bg-warning/10'; // Yellow background for masking info
    }
    return 'bg-surface-dim'; // Default background
  }, [isSuccess, hasMaskingInfo]);

  // Determine details text color
  const detailsTextColor = useMemo(() => {
    if (!isSuccess) {
      return 'text-negative';
    }
    if (hasMaskingInfo) {
      return 'text-on-surface';
    }
    return 'text-on-surface';
  }, [isSuccess, hasMaskingInfo]);

  return {
    hasDetails,
    hasMaskingInfo,
    isDetailsExpanded,
    detailsBgColor,
    detailsTextColor,
    setIsDetailsExpanded
  };
}
